import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Leaf, CircleHelp, ShoppingBasket } from 'lucide-react';
import SymbolLegend from './SymbolLegend';
import { tl } from '../lib/terms';
import TREE from '../lib/featureTree.json';
import { openHelp } from './HelpBtn';
import { treePoster } from '../lib/treePoster';
import { playLinks } from '../lib/license';
import { I18N } from '../lib/i18n';

export const openTree = () => window.dispatchEvent(new Event('giss-tree'));

const Leaves = ({ items, testid, hit }) => (
  <ul className="ft-leaves" data-testid={`${testid}-leaves`}>
    {items.map((n, i) => (n.k === 'h' ? <li key={n.t} className="ft-sub" data-testid={`${testid}-sub-${i}`}>{n.t}</li> : (
      <li key={n.t}>
        <button type="button" className={`ft-leaf ${n.h ? 'has-help' : ''} ${hit && hit(n.t) ? 'ft-hit' : ''}`} data-testid={`${testid}-leaf-${i}`} disabled={!n.h} onClick={() => openHelp(n.h)}>
          <Leaf size={13} /> <span>{n.t}</span> {n.h && <CircleHelp size={13} className="ft-q" />}
        </button>
      </li>
    )))}
  </ul>
);

const Node = ({ g, id, open, toggle, kind, delay, hit }) => (
  <div className={`ft-node ft-${kind} ${open ? 'open' : ''} ${hit && (hit(g.t) || hit(g.d)) ? 'ft-hit-node' : ''}`} style={{ animationDelay: `${delay}ms` }} data-testid={`ftree-${id}`}>
    <button type="button" className="ft-head" data-testid={`ftree-${id}-toggle`} aria-expanded={open} onClick={() => toggle(id)}>
      <span className="ft-name">{g.t}</span>
      <small>{g.d}</small>
      <ChevronDown size={16} className="ft-chev" />
    </button>
    <div className="ft-body"><div><Leaves items={g.c} testid={`ftree-${id}`} hit={hit} /></div></div>
  </div>
);

export default function FeatureTree({ lang }) {
  const [on, setOn] = useState(false);
  const [open, setOpen] = useState({});
  const [qs, setQs] = useState('');
  const [sym, setSym] = useState(false);
  const words = qs.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  const hit = words.length ? (txt) => words.every((w) => String(txt).toLowerCase().includes(w)) : null;
  const trunkRef = useRef(null);
  const d = TREE[lang] || TREE.de;
  const ids = [...d.crown.map((_, i) => `c${i}`), ...d.trunk.map((_, i) => `t${i}`), ...d.roots.map((_, i) => `r${i}`)];
  const all = ids.every((k) => open[k]);
  useEffect(() => { const go = () => { setOpen({}); setQs(''); setOn(true); }; window.addEventListener('giss-tree', go); return () => window.removeEventListener('giss-tree', go); }, []);
  useEffect(() => { if (on) setTimeout(() => trunkRef.current?.scrollIntoView({ block: 'center' }), 60); }, [on]);
  useEffect(() => { if (!on) return undefined; const k = (e) => e.key === 'Escape' && setOn(false); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [on]);
  const groups = [...d.crown.map((g, i) => [`c${i}`, g]), ...d.trunk.map((g, i) => [`t${i}`, g]), ...d.roots.map((g, i) => [`r${i}`, g])];
  const search = (v) => {
    setQs(v);
    const ws = v.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 1);
    if (!ws.length) return;
    const m = (x) => ws.every((w) => String(x).toLowerCase().includes(w));
    const found = groups.filter(([, g]) => m(g.t) || m(g.d) || g.c.some((n) => m(n.t))).map(([k]) => k);
    setOpen(Object.fromEntries(found.map((k) => [k, true])));
    if (found[0]) setTimeout(() => document.querySelector(`[data-testid="ftree-${found[0]}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 350);
  };
  const nHits = hit ? groups.reduce((a, [, g]) => a + g.c.filter((n) => hit(n.t)).length + (hit(g.t) ? 1 : 0), 0) : 0;
  if (!on) return null;
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const N = (kind, list, p) => list.map((g, i) => <Node key={g.t} g={g} id={`${p}${i}`} open={!!open[`${p}${i}`]} toggle={toggle} kind={kind} delay={i * 70} hit={hit} />);
  return createPortal(
    <div className="ft-overlay" data-testid="ftree-overlay">
      <div className="ft-bar">
        <b>🌳 {d.ui.title}</b>
        <button className="btn btn-outline btn-sm" data-testid="ftree-expand-btn" onClick={() => setOpen(all ? {} : Object.fromEntries(ids.map((k) => [k, true])))}>{all ? d.ui.fold : d.ui.open}</button>
        <button className="btn btn-proj btn-sm" data-testid="ftree-poster-btn" onClick={() => treePoster({ tree: d, url: playLinks().open, t: I18N[lang] || I18N.de })}>{d.ui.poster}</button>
        <button className="btn btn-proj btn-sm" data-testid="ftree-poster-a1-btn" onClick={() => treePoster({ tree: d, url: playLinks().open, t: I18N[lang] || I18N.de, size: 'a1' })}>{d.ui.poster.replace('A2', 'A1')}</button>
        <button className="icon-btn" data-testid="ftree-close-btn" onClick={() => setOn(false)}><X size={20} /></button>
      </div>
      <div className="ft-scroll">
        <div className="ft-search">
          <input type="search" data-testid="ftree-search-input" value={qs} placeholder={d.ui.search} onChange={(e) => search(e.target.value)} />
          {hit && <small data-testid="ftree-search-count">{nHits}</small>}
        </div>
        <p className="ft-hint">{d.ui.hint}</p>
        <section className="ft-crown" data-testid="ftree-crown">
          <h5>{d.ui.crown}</h5>
          <div className="ft-grid">{N('branch', d.crown, 'c')}</div>
        </section>
        <section className="ft-trunk" ref={trunkRef} data-testid="ftree-trunk">
          <div className="ft-core" data-testid="ftree-core"><span>{d.core}</span><small>{d.ui.trunk}</small></div>
          <div className="ft-rings">{N('ring', d.trunk, 't')}</div>
        </section>
        <section className="ft-roots" data-testid="ftree-roots">
          <svg className="ft-rootlines" viewBox="0 0 600 70" preserveAspectRatio="none" aria-hidden="true">
            <path d="M300 0 C300 40 120 30 100 70" /><path d="M300 0 L300 70" /><path d="M300 0 C300 40 480 30 500 70" />
            <path className="thin" d="M300 10 C260 50 200 50 180 70" /><path className="thin" d="M300 10 C340 50 400 50 420 70" />
          </svg>
          <h5>{d.ui.roots}</h5>
          <div className="ft-grid ft-grid3">{N('root', d.roots, 'r')}</div>
        </section>
      </div>
      {all && <button className="ft-basket" data-testid="ftree-basket-btn" title={I18N[lang]?.symTitle} onClick={() => setSym(true)}><i className="ft-basket-ic"><ShoppingBasket size={24} /></i><span>{(I18N[lang] || I18N.de).slBasket}</span></button>}
      {sym && <SymbolLegend t={tl(lang)} onClose={() => setSym(false)} />}
    </div>,
    document.body,
  );
}
